import { Link, useNavigate } from "react-router-dom";
import { useAuth, homePathFor } from "../contexts/AuthContext";
import { Bell, LogOut, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import API from "../lib/api";
import { OihMark } from "./OihMark";
import logger from "../lib/logger";

const NAV_BY_ROLE = {
  public: [
    { to: "/collection", label: "Explore Collection", testid: "nav-collection" },
    { to: "/participate", label: "Share Your Heritage", testid: "nav-participate" },
    { to: "/about",       label: "About",              testid: "nav-about" },
    { to: "/contact",     label: "Contact",            testid: "nav-contact" },
  ],
  member: [
    { to: "/member",              label: "Dashboard",      testid: "nav-member-dashboard" },
    { to: "/member/submissions",  label: "My Submissions", testid: "nav-member-subs" },
    { to: "/member/submissions/new", label: "New Submission", testid: "nav-member-new", cta: true },
    { to: "/collection",          label: "Collection",     testid: "nav-collection" },
  ],
  validator: [
    { to: "/validator", label: "Assigned Reviews", testid: "nav-val-dashboard" },
    { to: "/collection", label: "Collection",       testid: "nav-collection" },
  ],
  staff: [
    { to: "/staff",               label: "Dashboard",   testid: "nav-staff-dashboard" },
    { to: "/staff/submissions",   label: "Submissions", testid: "nav-staff-subs" },
    { to: "/staff/tasks",         label: "Requests",    testid: "nav-staff-tasks" },
    { to: "/collection",          label: "Collection",  testid: "nav-collection" },
  ],
  admin: [
    { to: "/admin",              label: "Dashboard",   testid: "nav-admin-dashboard" },
    { to: "/staff/submissions",  label: "Submissions", testid: "nav-admin-subs" },
    { to: "/staff/tasks",        label: "Requests",    testid: "nav-admin-tasks" },
    { to: "/admin/users",        label: "Users",       testid: "nav-admin-users" },
    { to: "/collection",         label: "Collection",  testid: "nav-collection" },
    { to: "/admin/settings",     label: "Settings",    testid: "nav-admin-settings" },
  ],
};

export default function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role || "public";
  const items = NAV_BY_ROLE[role] || NAV_BY_ROLE.public;

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await API.get("/notifications", { params: { unread_only: true } });
        if (alive) setUnread(data.unread || 0);
      } catch (e) { logger.error("Notification poll failed:", e); }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="border-b border-heritage bg-paper/95 backdrop-blur sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to={user ? homePathFor(user) : "/"} className="flex items-center gap-3 flex-shrink-0" data-testid="nav-logo">
          <OihMark size={40} tone="light" />
          <div className="hidden sm:block">
            <div className="font-display text-lg leading-tight text-navy">Office of Ismaili Heritage</div>
            <div className="text-[10px] text-muted-h tracking-[0.15em] uppercase">Preserving Our Stories</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className={`px-3 py-2 rounded-lg text-[14.5px] transition ${
                it.cta
                  ? "bg-[var(--oih-gold)] text-[var(--oih-navy-deep)] font-semibold hover:bg-[var(--oih-bronze)] hover:text-[var(--oih-cream)]"
                  : "text-ink-soft hover:text-navy hover:bg-[var(--oih-parchment)]"
              }`}
              data-testid={it.testid}
            >
              {it.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 flex-shrink-0">
          {user ? (
            <>
              <Link to="/notifications" className="relative p-2 rounded-full hover:bg-[var(--oih-parchment)] transition" data-testid="nav-notifications" aria-label="Notifications">
                <Bell size={20} className="text-ink-soft" />
                {unread > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--oih-gold)] text-[var(--oih-navy-deep)] text-[10px] font-bold flex items-center justify-center"
                    data-testid="nav-notifications-badge"
                    aria-label={`${unread} unread notifications`}
                  >
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </Link>
              <div className="hidden md:flex items-center gap-2 pl-3 border-l border-heritage">
                <div className="text-right">
                  <div className="text-xs text-muted-h leading-none">Signed in as</div>
                  <div className="text-sm text-navy font-medium leading-tight">{user.first_name}</div>
                </div>
                <button onClick={doLogout} className="p-2 rounded-lg hover:bg-[var(--oih-parchment)] transition" data-testid="logout-button" aria-label="Sign out">
                  <LogOut size={16} className="text-ink-soft" />
                </button>
              </div>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-[var(--oih-parchment)]"
                aria-label="Menu"
                aria-expanded={mobileOpen}
                data-testid="nav-mobile-toggle"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost hidden sm:inline-flex" data-testid="nav-login">Sign In</Link>
              <Link to="/participate" className="btn-primary text-sm px-4" data-testid="nav-register">
                Share Your Heritage
              </Link>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-[var(--oih-parchment)]"
                aria-label="Menu"
                data-testid="nav-mobile-toggle"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-heritage bg-paper" data-testid="nav-mobile-panel">
          <nav className="px-4 py-3 space-y-1">
            {items.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-[15px] ${
                  it.cta ? "bg-[var(--oih-gold)] text-[var(--oih-navy-deep)] font-semibold"
                          : "text-ink-soft hover:bg-[var(--oih-parchment)]"
                }`}
              >
                {it.label}
              </Link>
            ))}
            {user ? (
              <button onClick={doLogout} className="block w-full text-left px-3 py-2.5 rounded-lg text-[15px] text-ink-soft hover:bg-[var(--oih-parchment)]" data-testid="logout-button-mobile">
                Sign out
              </button>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2.5 rounded-lg text-[15px] text-ink-soft hover:bg-[var(--oih-parchment)]">
                Sign In
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
